/**
 * Pie Chart Map Marker for countries with multiple tiers
 */

import { getTierColor } from '../../../services/TierColorService';
import { 
  calculateTierDistribution, 
  generatePieSlices, 
  hasMultipleTiers 
} from './pieChartUtils';

/**
 * Class to create and manage pie chart map markers for multi-tier countries
 */
class PieChartMarker {
  /**
   * Create a new pie chart map marker
   * @param {Object} options - Configuration options
   * @param {Object} options.country - Country data
   * @param {number} options.maxTotal - Maximum total value for all countries
   * @param {number} options.maxLargest - Maximum largest value for all countries
   * @param {number} options.zoom - Current map zoom level
   * @param {Object} options.L - Leaflet library instance
   * @param {Object} options.layer - Map layer to add the marker to
   * @param {string|null} options.activeTierFilter - Active tier filter for color changes
   * @param {boolean} options.showTimezones - Whether timezone information should be shown
   * @param {Array} options.clientData - Client data for tier calculations
   * @param {Function} options.onSelect - Callback when marker is selected
   */
  constructor({ country, maxTotal, maxLargest, zoom, L, layer, activeTierFilter, showTimezones, clientData, onSelect }) {
    this.country = country;
    this.maxTotal = maxTotal;
    this.maxLargest = maxLargest;
    this.zoom = zoom;
    this.L = L;
    this.layer = layer;
    this.activeTierFilter = activeTierFilter;
    this.showTimezones = showTimezones;
    this.clientData = clientData;
    this.onSelect = onSelect;
    
    // Calculate tier distribution for this country
    this.tierDistribution = calculateTierDistribution(clientData, country.country);
  }
  
  /**
   * Calculate the radius for a circle based on value
   * @param {number} value - Value to scale
   * @param {number} maxValue - Maximum value for scaling
   * @param {number} minRadius - Minimum radius
   * @param {number} scale - Scale factor
   * @returns {number} Calculated radius
   */
  calcRadius(value, maxValue, minRadius, scale) {
    const base = minRadius + (value / maxValue) * scale;
    
    // Progressive scaling to prevent map clutter at low zoom levels
    let effZ;
    let adjustedMinRadius = minRadius;
    
    if (this.zoom >= 3) {
      effZ = 3; // Normal scaling for zoom 3+
    } else if (this.zoom === 2) {
      effZ = 20; // Very small circles for world view
      adjustedMinRadius = Math.min(minRadius, 2);
    } else {
      effZ = 40; // Extremely tiny circles for maximum zoom out
      adjustedMinRadius = Math.min(minRadius, 1);
    }
    
    const factor = 2 / Math.max(effZ, 1);
    return Math.max(adjustedMinRadius, Math.min(base * factor, scale));
  }
  
  /**
   * Get timezone information for this country from client data
   * @returns {Object} Timezone information
   */
  getCountryTimezone() {
    if (!this.clientData || !this.clientData.length) {
      return { timezone: 'N/A', localTime: 'N/A' };
    }
    
    // Find clients from this country
    const countryClients = this.clientData.filter(client => 
      client.country === this.country.country
    );
    
    if (countryClients.length === 0) {
      return { timezone: 'N/A', localTime: 'N/A' };
    }
    
    // Get timezone from the first client
    const firstClient = countryClients[0];
    const timezone = firstClient['Time zone'] || 'N/A';
    
    // Calculate local time if we have timezone info
    let localTime = 'N/A';
    if (timezone && timezone !== 'N/A') {
      try {
        const now = new Date();
        if (timezone.startsWith('UTC')) {
          const offsetStr = timezone.substring(3);
          if (offsetStr) {
            const offsetHours = parseFloat(offsetStr);
            const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
            const localTimeMs = utcTime + (offsetHours * 3600000);
            const localDate = new Date(localTimeMs);
            
            localTime = localDate.toLocaleString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
              weekday: 'short'
            });
          }
        }
      } catch (error) {
        console.warn('Error calculating local time:', error);
        localTime = 'Time unavailable';
      }
    }
    
    return { timezone, localTime };
  }
  
  /**
   * Create tier breakdown HTML for popup
   * @returns {string} HTML content for tier breakdown
   */
  createTierBreakdownHtml() {
    if (!this.tierDistribution.tiers || this.tierDistribution.tiers.length === 0) {
      return '';
    }
    
    let breakdownHtml = `
      <hr style="margin: 8px 0; border: none; border-top: 1px solid #ddd;">
      <p style="margin: 3px 0 6px 0; font-size: 13px; line-height: 1.3; font-weight: 600;">Tier Breakdown:</p>
    `;
    
    this.tierDistribution.tiers.forEach(({ tier, count, percentage }) => {
      const tierColor = getTierColor(tier, 'dark');
      breakdownHtml += `
        <p style="margin: 2px 0; font-size: 13px; line-height: 1.3;">
          <span style="display: inline-block; width: 12px; height: 12px; background-color: ${tierColor}; border-radius: 2px; margin-right: 6px; vertical-align: middle;"></span>
          <strong>Tier ${tier}:</strong> ${count} clients (${percentage.toFixed(1)}%)
        </p>
      `;
    });
    
    return breakdownHtml;
  }
  
  /**
   * Create popup HTML content
   * @returns {string} HTML content for popup
   */
  createPopupContent() {
    const { country, dominantManager, managers, total, largest } = this.country;
    const others = managers.filter(m => m !== dominantManager);
    
    let popupHtml = `
      <div class="info-popup">
        <h3 style="margin: 0 0 8px 0; font-size: 16px; color: #333; font-weight: 600;">${country}</h3>
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Total Groups:</strong> ${total}</p>
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Largest Group:</strong> ${largest}</p>
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Largest Manager:</strong> <span style="color:#0074D9; font-weight:bold;">${dominantManager}</span></p>
    `;
    
    if (others.length) {
      popupHtml += `<p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Other Managers:</strong> ${others.join(', ')}</p>`;
    }
    
    // Add tier breakdown
    popupHtml += this.createTierBreakdownHtml();
    
    // Add timezone information if Show Zones is active
    if (this.showTimezones) {
      const timezoneInfo = this.getCountryTimezone();
      popupHtml += `
        <hr style="margin: 8px 0; border: none; border-top: 1px solid #ddd;">
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Timezone:</strong> <span style="color: #0074D9; font-weight: bold;">${timezoneInfo.timezone}</span></p>
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Local Time:</strong> ${timezoneInfo.localTime}</p>
      `;
    }
    
    popupHtml += `</div>`;
    return popupHtml;
  }
  
  /**
   * Create SVG pie chart icon with perfect circular appearance
   * @param {number} radius - Radius of the pie chart
   * @returns {string} SVG string for the pie chart
   */
  createPieChartSvg(radius) {
    const slices = generatePieSlices(this.tierDistribution.tiers, radius);
    const size = radius * 2;
    const centerX = radius;
    const centerY = radius;
    
    // Create SVG with improved rendering properties for smooth circles
    let svgContent = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg" style="shape-rendering: geometricPrecision;">`;
    
    // Add circular clipping path to ensure perfect circle boundary
    svgContent += `
      <defs>
        <clipPath id="circleClip-${radius}">
          <circle cx="${centerX}" cy="${centerY}" r="${radius - 0.5}"/>
        </clipPath>
      </defs>
    `;
    
    // Group all slices with clipping
    svgContent += `<g clip-path="url(#circleClip-${radius})">`;
    
    // Add each pie slice with no stroke to prevent jagged edges
    slices.forEach(({ tier, path }) => {
      const fillColor = this.activeTierFilter 
        ? getTierColor(this.activeTierFilter, 'dark')
        : getTierColor(tier, 'dark');
      
      svgContent += `<path d="${path}" fill="${fillColor}" stroke="none"/>`;
    });
    
    svgContent += '</g>';
    
    // Add perfect circular border on top to ensure smooth circumference
    svgContent += `<circle cx="${centerX}" cy="${centerY}" r="${radius - 0.5}" fill="none" stroke="#333" stroke-width="1" style="shape-rendering: geometricPrecision;"/>`;
    
    svgContent += '</svg>';
    return svgContent;
  }
  
  /**
   * Add the pie chart marker to the map
   */
  addToMap() {
    const { L, layer, country, maxTotal, onSelect } = this;
    const { latitude, longitude } = country;
    
    // Calculate marker radius based on total clients
    const markerRadius = this.calcRadius(country.total, maxTotal, 8, 25);
    
    // Create SVG pie chart
    const svgString = this.createPieChartSvg(markerRadius);
    const svgUrl = 'data:image/svg+xml;base64,' + btoa(svgString);
    
    // Create custom icon
    const pieIcon = L.icon({
      iconUrl: svgUrl,
      iconSize: [markerRadius * 2, markerRadius * 2],
      iconAnchor: [markerRadius, markerRadius],
      popupAnchor: [0, -markerRadius]
    });
    
    // Create marker
    const marker = L.marker([latitude, longitude], { icon: pieIcon }).addTo(layer);
    
    // Create popup
    const popupContent = this.createPopupContent();
    const popup = L.popup().setContent(popupContent);
    
    // Bind popup and click handler
    marker.bindPopup(popup);
    marker.on('click', onSelect);
    
    // Store marker reference
    this.marker = marker;
    
    return this;
  }
  
  /**
   * Remove the marker from the map
   */
  remove() {
    if (this.marker) {
      this.marker.remove();
    }
  }
  
  /**
   * Check if this country should use a pie chart marker
   * @param {Array} clientData - Client data array
   * @param {string} countryName - Country name
   * @returns {boolean} True if country has multiple tiers
   */
  static shouldUsePieChart(clientData, countryName) {
    return hasMultipleTiers(clientData, countryName);
  }
}

export default PieChartMarker;
